import React, { useState } from "react";
import Datepicker from "react-datepicker";
import PropTypes from "prop-types";
import { FaCalendarAlt } from "react-icons/fa";
import {
  dMMMyyyy,
  DEFAULT_REPAYMENT_DATE_EXTENSION_DAYS,
  EXTENDED_REPAYMENT_DATE_EXTENSION_DAYS
} from "../../utils/constants/appConstants";
import {
  getRepaymentFloorDate,
  isRepaymentDateLocked,
  isWeekendNZT
} from "../../utils/utils";
import "react-datepicker/dist/react-datepicker.css";
import Style from "./datepicker.module.scss";

const FundedDatePicker = ({
  onChange,
  selected,
  originalDueDate,
  isExtendedRepaymentDateValidation,
  disabled,
  countryCode,
  tooltipId,
  tooltipContent
}) => {
  const [selectedDate, setSelectedDate] = useState(selected);

  // Ensure no time part when comparing dates
  const truncateTime = (date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  // Lock the picker entirely when the customer can no longer change the date
  // (status-based from the parent, or the country-specific cutoff).
  const isDatepickerDisabled =
    disabled || isRepaymentDateLocked(selected, countryCode);

  // Earliest a repayment can be brought forward to: the next business day
  const minSelectableDate = truncateTime(getRepaymentFloorDate());

  // Latest a repayment can be pushed to: 5 business days after the original
  // due date, or 10 once the customer has 2+ completed funding rounds
  // (isExtendedRepaymentDateValidation, from the quotes-by-user response).
  // Weekends are skipped so a weekend never eats into the allowance.
  const allowedBusinessDays = isExtendedRepaymentDateValidation
    ? EXTENDED_REPAYMENT_DATE_EXTENSION_DAYS
    : DEFAULT_REPAYMENT_DATE_EXTENSION_DAYS;
  const maxSelectableDate = new Date(originalDueDate);
  let businessDaysAdded = 0;
  while (businessDaysAdded < allowedBusinessDays) {
    maxSelectableDate.setDate(maxSelectableDate.getDate() + 1);
    if (!isWeekendNZT(maxSelectableDate)) businessDaysAdded += 1;
  }

  const filterDate = (date) => {
    const d = truncateTime(date);
    // Date must be within range AND not be a weekend
    return (
      d >= minSelectableDate && d <= maxSelectableDate && !isWeekendNZT(date)
    );
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    onChange(date);
  };

  return (
    <div
      className={Style.datepickerWithicon}
      data-tooltip-id={tooltipId}
      data-tooltip-content={tooltipContent}
    >
      <Datepicker
        className={`${Style.datepicercustom} ${Style.fundedRepayDate}`}
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat={dMMMyyyy}
        minDate={minSelectableDate}
        maxDate={maxSelectableDate}
        filterDate={filterDate}
        disabled={isDatepickerDisabled}
      />
      <span>
        <FaCalendarAlt />
      </span>
    </div>
  );
};
FundedDatePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.instanceOf(Date),
  originalDueDate: PropTypes.instanceOf(Date),
  isExtendedRepaymentDateValidation: PropTypes.bool,
  disabled: PropTypes.bool,
  countryCode: PropTypes.string,
  tooltipId: PropTypes.string,
  tooltipContent: PropTypes.string
};

export default FundedDatePicker;
